import { useSelector } from 'react-redux';
import { selectCurrUserPermission } from '@/store/bootstrap/selectors';

import { Flex, Box } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks';

// Returns `true` if `permissions` satisfies the permission `spec`, `false` otherwise.
//
// EXAMPLES
//
// For single permission check:
//   checkPermissions(permissions, 'some.action?')
//
// For multi-permission check (where all must pass):
//   checkPermissions(permissions, { all: ['some.action?', 'other.action?', ...] })
//
// For multi-permission check (where at least one must pass):
//   checkPermissions(permissions, { any: ['some.action?', 'other.action?', ...] })
//
// NOTES
//
// If both `any` and `all` are specified, `true` is returned only when both are satisfied.
//
export function checkPermissions(permissions = {}, spec) {
  // single permission check
  if (typeof spec === 'string') {
    return permissions[spec];
  }

  // multi-permission check
  const { any, all } = spec;
  return (
    (any ? any.some((perm) => permissions[perm]) : true) &&
    (all ? all.every((perm) => permissions[perm]) : true)
  );
}

export const usePermission = ({ permission = null }) => {
  const permissions = useSelector(selectCurrUserPermission);
  if (!permission) {
    console.warn('permission property is needed for PermissionGuard');
    return null;
  }
  return checkPermissions(permissions, permission);
};

export const PermissionGuard = ({
  permission,
  featureFlag = null,
  children,
}) => {
  const { currUser } = useAuth();
  const isfeatureEnabled =
    typeof featureFlag === 'string'
      ? currUser?.feature_options?.[featureFlag] || currUser[featureFlag]
      : true;

  const hasPermission = usePermission({ permission });
  if (!(hasPermission && isfeatureEnabled)) return null;
  return <>{children}</>;
};

export const PageGuard = ({
  permission = null,
  featureFlag = null,
  children,
}) => {
  const { currUser } = useAuth();
  const isfeatureEnabled =
    typeof featureFlag === 'string'
      ? currUser?.feature_options?.[featureFlag] || currUser[featureFlag]
      : true;
  const hasPermission = usePermission({ permission });
  const isPermissionEnabled =
    typeof permission === 'string' ? hasPermission : true;

  if (!(isPermissionEnabled && isfeatureEnabled)) {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        flexDir="column"
        height="full"
      >
        <Box>Sorry, you do not have permission to visit this page</Box>
        <Box fontWeight="semiBold" color="brand.100" px="4px">
          <Link to="/dashboard">Go to Dashboard</Link>
        </Box>
      </Flex>
    );
  }
  return <>{children}</>;
};
